import { styled } from 'dripsy';
import React from 'react';
import { ModalComponentProp } from 'react-native-modalfy';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { observer, useLocalObservable } from 'mobx-react-lite';

import { ModalStackParamsType } from '../../modals/types';
import { ModalView } from './components/ModalView';
import { BaseText } from '../common/BaseText';
import { Separator } from '../common/Separator';
import { IconTouchable } from '../common/IconTouchable';
import { useTracksModel } from '@models/Tracks/TracksModel';
import { ButtonsLayout } from '../layouts/ButtonsLayout';
import { Button, ButtonStyleEnum } from '../common/Button';
import { OptionItem } from '@components/common/OptionItem';
import { modals } from '@modals';
import { IS_WEB } from '@utils';
import { TrackFeedbackSubmitOptionEnum } from '@api';

const ModalViewContainer = styled(ModalView)({
  py: '$8',
  px: '$8',
  width: [289, 391],
});

const TitleContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Title = styled(BaseText)({
  variant: 'text.xlarge',
  fontWeight: 'semi',
});

const Description = styled(BaseText)({
  variant: 'text.medium',
  color: '$text400',
});

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'ChooseTrackFeedbackOptionModal'
>;

interface IChooseTrackFeedbackOptionModalStore {
  optionType: TrackFeedbackSubmitOptionEnum | null;
  setOptionType(id: TrackFeedbackSubmitOptionEnum | null): void;
  isSelectedOption: boolean;
}

export interface IChooseTrackFeedbackOptionModalParams {
  onNavigateToTrackFeedback(
    option: TrackFeedbackSubmitOptionEnum | null,
  ): void;
}

export const ChooseTrackFeedbackOptionModal = observer(
  ({ modal }: ModalPropsType) => {
    const { t } = useTranslation();

    const store =
      useLocalObservable<IChooseTrackFeedbackOptionModalStore>(
        () => ({
          optionType: null,

          setOptionType(id: number) {
            this.optionType = id;
          },

          get isSelectedOption() {
            return this.optionType !== null;
          },
        }),
      );

    function selectOption(value: null | number) {
      if (store.optionType === value) {
        store.setOptionType(null);
        return;
      }

      store.setOptionType(value);
    }

    function handleOnPress() {
      if (store.optionType === null) {
        return;
      }

      confirmAction();
      closeModal();
    }
    const track = useTracksModel();

    const isFreeOptionDisabled = !track.feedback.isNewPeriod;
    const isCreditOptionDisabled = !track.feedback.credits?.count;
    const onNavigateToTrackFeedback = modal.getParam(
      'onNavigateToTrackFeedback',
    );

    function closeModal() {
      modal.closeModal();
    }

    function cancelAction() {
      closeModal();
    }

    function confirmAction() {
      if (IS_WEB) {
        modal.openModal(modals.TrackFeedbackFormModal, {
          cancelAction,
          feedbackOptionType: store.optionType,
        });
      } else {
        onNavigateToTrackFeedback(store.optionType);
      }
    }

    return (
      <ModalViewContainer>
        <TitleContainer>
          <Title>{t('common.chooseTrackFeedbackOption.title')}</Title>

          <IconTouchable
            onPress={closeModal}
            name="close-light"
            size={24}
          />
        </TitleContainer>
        <Separator />
        <Description>
          {t('common.chooseTrackFeedbackOption.description')}
        </Description>

        <Separator />

        <OptionItem
          onValueChange={() =>
            selectOption(TrackFeedbackSubmitOptionEnum.Credit)
          }
          text={t('common.chooseTrackFeedbackOption.oneCredit')}
          value={
            store.optionType === TrackFeedbackSubmitOptionEnum.Credit
          }
          disabled={isCreditOptionDisabled}
        />

        <Separator size="$5" />

        <OptionItem
          onValueChange={() =>
            selectOption(TrackFeedbackSubmitOptionEnum.Free)
          }
          text={t('common.chooseTrackFeedbackOption.freeOption')}
          value={
            store.optionType === TrackFeedbackSubmitOptionEnum.Free
          }
          disabled={isFreeOptionDisabled}
        />

        <Separator />

        <ButtonsLayout>
          <Button
            onPress={handleOnPress}
            disabled={!store.isSelectedOption}
          >
            {t('common.chooseTrackFeedbackOption.submit')}
          </Button>

          <Button
            onPress={cancelAction}
            buttonStyle={ButtonStyleEnum.Outline}
          >
            {t('common.chooseTrackFeedbackOption.cancel')}
          </Button>
        </ButtonsLayout>
      </ModalViewContainer>
    );
  },
);
